import React, { useContext, useState, useMemo } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { Autocomplete, TextField } from "@mui/material";
import { ThemeContext } from 'styled-components';
import { UserPlus, X } from '@phosphor-icons/react';
import { IsValidEmail } from '../../../utils/Validators';
import { getGroupParticipantsExcludingBill } from '../../../services/group/Groups';
import { useQuery } from 'react-query';
import { CsrfContext } from '../../../providers/CsrfProvider';
import TableWithActions from '../../common/components/Wrappers/TableWithActions';

const ParticipantsList = ({ 
    templateId, 
    groupId, 
    participants = [], 
    onAddParticipant,
    onRemoveParticipant,
    disabled = false,
    isLoading = false
}) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const [email, setEmail] = useState('');
    const [dropdownEverOpened, setDropdownEverOpened] = useState(false);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);

    // Fetch group members (excluding current bill participants) when user opens the participant dropdown
    const effectiveTemplateId = templateId && templateId !== 'none' ? templateId : 'none';
    const { data: groupMembers, isLoading: loadingGroupMembers, error: groupMembersError } = useQuery(
        ['groupParticipantsExcludingBill', groupId, effectiveTemplateId],
        () => getGroupParticipantsExcludingBill(groupId, effectiveTemplateId, csrfToken),
        {
            enabled: !!groupId && !!csrfToken && dropdownEverOpened,
            staleTime: 0,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );

    // Filter out existing participants from group members
    const existingParticipantEmails = useMemo(() => {
        return new Set(
            (participants || []).map(p => 
                (typeof p === 'string' ? p : p.email || '').toLowerCase()
            )
        );
    }, [participants]);

    const filteredGroupMembers = useMemo(() => {
        if (!groupMembers || loadingGroupMembers) return [];
        // Ensure groupMembers is an array
        const membersArray = Array.isArray(groupMembers) ? groupMembers : [];
        return membersArray.filter(member => {
            const memberEmail = (typeof member === 'string' ? member : member.email || '').toLowerCase();
            return !existingParticipantEmails.has(memberEmail);
        });
    }, [groupMembers, existingParticipantEmails, loadingGroupMembers]);

    // Debug logging
    React.useEffect(() => {
        console.log('ParticipantsList - groupId:', groupId, 'templateId:', templateId, 'effectiveTemplateId:', effectiveTemplateId);
        console.log('ParticipantsList - groupMembers:', groupMembers, 'loading:', loadingGroupMembers, 'error:', groupMembersError);
        console.log('ParticipantsList - filteredGroupMembers:', filteredGroupMembers);
        console.log('ParticipantsList - query enabled:', !!groupId && !!csrfToken);
    }, [groupId, templateId, effectiveTemplateId, groupMembers, loadingGroupMembers, groupMembersError, filteredGroupMembers, csrfToken]);

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
            setEmail(newInputValue);
        }
    };

    const handleAutocompleteChange = async (event, value) => {
        if (value) {
            const emailValue = typeof value === 'string' ? value : value.email;
            if (emailValue && emailValue.trim() !== '' && IsValidEmail(emailValue.trim())) {
                await onAddParticipant({ email: emailValue.trim() });
                setEmail('');
            }
        }
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        const emailValue = email.trim();
        if (emailValue && IsValidEmail(emailValue)) {
            await onAddParticipant({ email: emailValue });
            setEmail('');
        }
    };

    const isValidEmail = email.trim() !== '' && IsValidEmail(email.trim());

    return (
        <Box
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                mb: '40px', // Standardized bottom spacing to match top spacing
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <TableWithActions
                showLeftAction="buffer"
                showRightAction={true}
                actionColumnWidth="56px"
                forceSpacing={true}
                containerProps={{
                    component: Paper,
                    sx: {
                        mb: 0,
                        borderRadius: 2,
                        backgroundColor: theme.colors.tableBackground || theme.colors.background,
                        boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        '& .MuiTable-root': {
                            '& .MuiTableBody-root': {
                                '& .MuiTableRow-root:last-child': {
                                    '& .MuiTableCell-root': {
                                        borderBottom: 'none',
                                    },
                                },
                            },
                        },
                    }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                        <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem', pl: 0, py: 2 }}>
                            <Typography sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem' }}>Participants</Typography>
                        </TableCell>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                        <TableCell sx={{ color: theme.colors.text, fontWeight: 600, pl: 0 }}>
                            {!disabled && (
                                <Autocomplete
                                    freeSolo
                                    open={autocompleteOpen}
                                    onOpen={() => { setAutocompleteOpen(true); setDropdownEverOpened(true); }}
                                    onClose={() => setAutocompleteOpen(false)}
                                    options={filteredGroupMembers}
                                    loading={loadingGroupMembers}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') {
                                            return option;
                                        }
                                        return option.email || '';
                                    }}
                                    inputValue={email}
                                    onInputChange={handleInputChange}
                                    onChange={handleAutocompleteChange}
                                    filterOptions={(options, params) => {
                                        const filtered = options.filter((option) => {
                                            const emailValue = typeof option === 'string' ? option : option.email;
                                            return emailValue.toLowerCase().includes(params.inputValue.toLowerCase());
                                        });
                                        return filtered;
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Type to search or enter email"
                                            disabled={disabled || isLoading}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSubmit(null);
                                                }
                                            }}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.colors.borderContrast,
                                                    fontSize: '16px', // Minimum 16px to prevent iOS zoom
                                                    '& fieldset': {
                                                        borderColor: theme.colors.borderContrast,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.colors.borderContrast,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.colors.borderContrast,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.colors.borderContrast,
                                                    fontSize: '16px', // Minimum 16px to prevent iOS zoom
                                                    '&.Mui-focused': {
                                                        color: theme.colors.borderContrast,
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '16px', // Minimum 16px to prevent iOS zoom
                                                },
                                            }}
                                        />
                                    )}
                                    loading={loadingGroupMembers}
                                    disabled={loadingGroupMembers || disabled || isLoading}
                                    sx={{ width: '100%' }}
                                />
                            )}
                        </TableCell>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                            {!disabled && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <IconButton
                                        onClick={handleSubmit}
                                        disabled={!isValidEmail || disabled || isLoading}
                                        size="small"
                                        sx={{
                                            color: 'inherit',
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover,
                                            },
                                            '&:disabled': {
                                                color: theme.colors.overlayDisabled,
                                            },
                                        }}
                                    >
                                        <UserPlus size={24} color={theme.colors.text} weight="bold" />
                                    </IconButton>
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {participants && Array.isArray(participants) && participants.map((user, index) => (
                        <TableRow key={user.id || user.email || `participant-${index}`}>
                            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                            <TableCell sx={{ color: theme.colors.text, pl: 0 }}>
                                <Typography sx={{ color: theme.colors.text, fontSize: '1.1rem' }}>
                                    {user.email}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <IconButton
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (onRemoveParticipant && !disabled && !isLoading) {
                                                onRemoveParticipant(user);
                                            }
                                        }}
                                        disabled={disabled || !onRemoveParticipant || isLoading}
                                        size="small"
                                        sx={{
                                            color: 'inherit',
                                            padding: '4px',
                                            cursor: (disabled || !onRemoveParticipant || isLoading) ? 'not-allowed' : 'pointer',
                                            '&:hover:not(:disabled)': {
                                                backgroundColor: theme.colors.overlayHover,
                                            },
                                            '&:disabled': {
                                                color: theme.colors.overlayDisabled,
                                                cursor: 'not-allowed',
                                            },
                                        }}
                                    >
                                        <X size={24} color={theme.colors.text} weight="bold" />
                                    </IconButton>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </TableWithActions>
        </Box>
    );
};

export default ParticipantsList;
